(() => {
  const homepage = () => document.body?.classList?.contains('template-index') === true
    || document.body?.dataset?.pageTemplate === 'index'
    || globalThis.location?.pathname === '/';

  // Never reorder the initial homepage DOM. This preserves the server-rendered LCP/CLS
  // contract on every viewport; collection merchandising remains available below.
  if (homepage()) return;

  const targets = [
    ['[data-dynamic-merch]', 0],
    ['.muaazon-subcats__grid', 8],
    ['.collection-list-grid', 0],
    ['[data-category-carousel-viewport]', 0],
    ['.collections-directory-grid', 0],
    ['.collection-category-rail__scroller', 0],
    ['.merchandising-product-grid', 0],
  ];

  const directChildren = (container) => Array.from(container.children).filter((node) => node.nodeType === 1);
  const pageSeed = (() => {
    try {
      const values = new Uint32Array(1);
      crypto.getRandomValues(values);
      return values[0] || Date.now();
    } catch {
      return Date.now();
    }
  })();

  const hash = (value) => {
    let result = pageSeed >>> 0;
    for (let index = 0; index < value.length; index += 1) result = Math.imul(result ^ value.charCodeAt(index), 16777619) >>> 0;
    return result || 1;
  };

  const shuffle = (items, seed) => {
    const list = [...items];
    let state = seed >>> 0;
    const random = () => {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      return state / 4294967296;
    };
    for (let index = list.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [list[index], list[swapIndex]] = [list[swapIndex], list[index]];
    }
    return list;
  };

  const updateCarouselProgress = (root) => {
    root.querySelectorAll?.('.category-carousel__progress').forEach((progress) => {
      directChildren(progress).filter((item) => item.matches('[data-category-carousel-dot]')).forEach((dot, index) => {
        if (index === 0) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    });
  };

  const rotateContainer = (container, fallbackLimit = 0) => {
    if (!container || container.dataset.dynamicMerchDone === 'true') return;
    const items = directChildren(container);
    if (items.length < 2) {
      container.dataset.dynamicMerchDone = 'true';
      return;
    }
    const pinned = items.filter((item) => item.dataset.dynamicPinned === 'true' || item.classList.contains('is-active'));
    const rotatable = items.filter((item) => !pinned.includes(item));
    const key = `${container.id || container.className || 'merch'}:${items.length}`;
    const ordered = [...pinned, ...shuffle(rotatable, hash(key))];
    const fragment = document.createDocumentFragment();
    ordered.forEach((item) => fragment.appendChild(item));
    container.appendChild(fragment);

    const limit = Number.parseInt(container.dataset.dynamicLimit || String(fallbackLimit || 0), 10) || 0;
    ordered.forEach((item, index) => {
      const visible = !limit || index < limit;
      item.hidden = !visible;
      if (visible) item.removeAttribute('aria-hidden');
      else item.setAttribute('aria-hidden', 'true');
    });
    container.dataset.dynamicMerchDone = 'true';
    updateCarouselProgress(container.closest?.('section') ?? document);
  };

  const observeContainer = (container, fallbackLimit = 0) => {
    if (!container || container.dataset.dynamicMerchDone === 'true') return;
    if (typeof IntersectionObserver !== 'function') return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry || entry.isIntersecting) return;
      observer.disconnect();
      rotateContainer(container, fallbackLimit);
    }, { threshold: 0 });
    observer.observe(container);
  };

  const apply = (root = document, { force = false } = {}) => {
    targets.forEach(([selector, limit]) => {
      root.querySelectorAll?.(selector).forEach((container) => {
        if (force) rotateContainer(container, limit);
        else observeContainer(container, limit);
      });
    });
  };

  const schedule = (root = document, options = {}) => {
    const run = () => apply(root, options);
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 800 });
    else window.setTimeout(run, 180);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => schedule(), { once: true });
  else schedule();
  document.addEventListener('shopify:section:load', (event) => schedule(event.target, { force: true }));
})();
